/**
 * AdminA2ManualRescue.jsx · v2.8.9.3
 * ────────────────────────────────────
 * Bandeja de OTs que el motor A2 no pudo agendar automáticamente.
 * El coordinador puede agendarlas manualmente forzando la cita si es necesario.
 * Modo agendamiento directo: la cita queda 'confirmada' al asignarse (sin WhatsApp de negociación).
 */
import { useEffect, useState, useCallback, useMemo } from "react";
import Layout from "../components/Layout";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import {
  AlertOctagon, Loader2, RefreshCw, Calendar, User, MapPin, Zap,
  X, CheckCircle2, Search, ShieldAlert, Clock, ArrowRight,
} from "lucide-react";

export default function AdminA2ManualRescue() {
  const { hasPerm } = useAuth();
  const canView = hasPerm ? hasPerm("M32.a2.view") : false;
  const canExecute = hasPerm ? hasPerm("M32.a2.execute") : false;

  const [items, setItems] = useState([]);
  const [breakdown, setBreakdown] = useState({});
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [q, setQ] = useState("");
  const [modalOt, setModalOt] = useState(null); // OT abierta en modal

  const fetchPending = useCallback(async (silent = false) => {
    if (!silent) setRefreshing(true);
    try {
      const { data } = await api.get("/a2/autopilot/manual-rescue/pending", { params: { limit: 500 } });
      setItems(data?.items || []);
      setBreakdown(data?.reason_breakdown || {});
    } catch (e) {
      if (!silent) toast.error(e?.response?.data?.detail || "No se pudo cargar la bandeja");
    } finally {
      setLoading(false);
      if (!silent) setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    if (!canView) { setLoading(false); return; }
    fetchPending();
    const iv = setInterval(() => fetchPending(true), 20000);
    return () => clearInterval(iv);
  }, [canView, fetchPending]);

  const filtered = useMemo(() => {
    if (!q) return items;
    const s = q.toLowerCase();
    return items.filter((it) =>
      (it.client_name || "").toLowerCase().includes(s) ||
      String(it.odoo_task_id || "").includes(s) ||
      (it.afc_code || "").toLowerCase().includes(s) ||
      (it.autopilot_fail_reason || "").toLowerCase().includes(s)
    );
  }, [items, q]);

  if (!canView) {
    return (
      <Layout title="Rescate Manual A2" subtitle="Acceso restringido">
        <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded" data-testid="rescue-403">
          Requiere permiso <code>M32.a2.view</code>.
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="Rescate Manual A2" subtitle="OTs que el motor no pudo procesar">
      <div className="space-y-4" data-testid="rescue-page">
        {/* Header KPI */}
        <div className="bg-gradient-to-r from-red-500 to-red-600 text-white rounded-xl p-5 flex items-center justify-between" data-testid="rescue-kpi-header">
          <div>
            <div className="text-[10px] uppercase tracking-widest opacity-80 font-semibold">OTs para Rescate Manual</div>
            <div className="text-4xl font-bold" style={{ fontFamily: "Space Grotesk" }}>{items.length}</div>
            <div className="text-xs mt-1 opacity-90">Requieren coordinación manual · el A2 no pudo agendarlas</div>
          </div>
          <ShieldAlert size={44} className="opacity-80" />
        </div>

        {/* Breakdown de razones */}
        {Object.keys(breakdown).length > 0 && (
          <div className="bg-white border border-gray-200 rounded-lg p-3" data-testid="rescue-breakdown">
            <div className="text-[10px] uppercase font-semibold text-gray-500 mb-2">Razones más comunes</div>
            <div className="flex flex-wrap gap-2">
              {Object.entries(breakdown).map(([reason, cnt]) => (
                <span key={reason} className="text-[11px] bg-red-50 text-red-800 border border-red-200 px-2 py-1 rounded">
                  <b>{cnt}</b> · {reason}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Toolbar */}
        <div className="bg-white border border-gray-200 rounded-lg p-3 flex items-center gap-2 flex-wrap">
          <div className="relative flex-1 min-w-[220px]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Filtrar por cliente, OT o razón…"
              className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-[#E0E621] focus:border-transparent"
              data-testid="rescue-search"
            />
          </div>
          <button
            onClick={() => fetchPending(false)}
            disabled={refreshing}
            className="flex items-center gap-1.5 px-3 py-2 rounded border border-gray-300 hover:bg-gray-50 text-sm disabled:opacity-40"
            data-testid="rescue-refresh"
          >
            {refreshing ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />}
            Refrescar
          </button>
        </div>

        {/* Tabla */}
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-testid="rescue-table">
          {loading ? (
            <div className="p-8 text-center text-gray-400 flex items-center justify-center gap-2">
              <Loader2 size={16} className="animate-spin" /> Cargando bandeja…
            </div>
          ) : filtered.length === 0 ? (
            <div className="p-8 text-center text-gray-500" data-testid="rescue-empty">
              <CheckCircle2 size={40} className="mx-auto text-emerald-400 mb-2" />
              <div className="font-semibold text-sm">Sin OTs pendientes de rescate</div>
              <div className="text-xs mt-1">El motor A2 está procesando todo automáticamente. 🎉</div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-[10px] uppercase tracking-widest text-gray-500 font-semibold">
                  <tr>
                    <th className="text-left px-3 py-2">ID OT</th>
                    <th className="text-left px-3 py-2">Cliente</th>
                    <th className="text-left px-3 py-2">Plan / Sucursal</th>
                    <th className="text-left px-3 py-2">Razón fallo A2</th>
                    <th className="text-left px-3 py-2">Último intento</th>
                    <th className="text-right px-3 py-2">Acción</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((it, i) => (
                    <tr key={it.odoo_task_id} className="border-t border-gray-100 hover:bg-red-50/40" data-testid={`rescue-row-${i}`}>
                      <td className="px-3 py-2 font-mono text-xs">
                        <div className="font-semibold text-[#0A3747]">{it.afc_code || `#${it.odoo_task_id}`}</div>
                        <div className="text-[10px] text-gray-400">OT #{it.odoo_task_id}</div>
                      </td>
                      <td className="px-3 py-2 text-xs">
                        <div className="font-medium text-gray-800">{it.client_name || "—"}</div>
                        {it.client_phone && (
                          <div className="text-[10px] text-gray-500 font-mono">{it.client_phone}</div>
                        )}
                      </td>
                      <td className="px-3 py-2 text-xs text-gray-600">
                        <div>{it.plan_service || "—"}</div>
                        <div className="text-[10px] text-gray-400">{it.branch_office_name || "—"}</div>
                      </td>
                      <td className="px-3 py-2">
                        <span className="text-[11px] font-semibold text-red-700 bg-red-50 border border-red-200 px-2 py-1 rounded-md inline-flex items-center gap-1"
                              data-testid={`rescue-reason-${i}`}>
                          <AlertOctagon size={11} /> {it.autopilot_fail_reason || "Sin razón"}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-[11px] font-mono text-gray-600 whitespace-nowrap">
                        {it.autopilot_last_attempt_at
                          ? new Date(it.autopilot_last_attempt_at).toLocaleString("es-EC", { timeZone: "America/Guayaquil", day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })
                          : "—"}
                      </td>
                      <td className="px-3 py-2 text-right">
                        {canExecute ? (
                          <button
                            onClick={() => setModalOt(it)}
                            data-testid={`rescue-schedule-btn-${i}`}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#E0E621] text-[#0A3747] rounded font-semibold text-xs hover:bg-[#c9cf1e]"
                          >
                            <Calendar size={12} /> Agendar Manualmente <ArrowRight size={11} />
                          </button>
                        ) : (
                          <span className="text-[10px] text-gray-400 italic">Sin permiso</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="text-[10px] text-gray-400 italic">
          💡 Modo agendamiento directo: la cita queda <b>«Confirmada»</b> al asignarse. WhatsApp sale solo como aviso informativo, sin esperar confirmación del cliente.
        </div>
      </div>

      {/* Modal Agendar Manualmente */}
      {modalOt && (
        <RescueScheduleModal
          ot={modalOt}
          onClose={() => setModalOt(null)}
          onSuccess={() => { setModalOt(null); fetchPending(); }}
        />
      )}
    </Layout>
  );
}

/** Modal con calendario + slot sugerido + opción de forzar buffer */
function RescueScheduleModal({ ot, onClose, onSuccess }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [suggestion, setSuggestion] = useState(null);
  const [techEmail, setTechEmail] = useState("");
  const [slotStart, setSlotStart] = useState("");
  const [slotEnd, setSlotEnd] = useState("");
  const [force, setForce] = useState(false);
  const [notes, setNotes] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/a2/autopilot/manual-rescue/${ot.odoo_task_id}/suggestion`);
        setSuggestion(data);
        const sug = data?.suggestion || {};
        if (sug.slot_start) setSlotStart(sug.slot_start.slice(0, 16));
        if (sug.slot_end) setSlotEnd(sug.slot_end.slice(0, 16));
      } catch (e) {
        toast.error("No se pudo cargar la sugerencia");
      } finally {
        setLoading(false);
      }
    })();
  }, [ot.odoo_task_id]);

  const submit = async () => {
    if (!techEmail.trim()) { toast.error("Ingresa el correo del técnico"); return; }
    if (!slotStart || !slotEnd) { toast.error("Define la fecha/hora de inicio y fin"); return; }
    setSaving(true);
    try {
      const iso = (s) => (s.length === 16 ? `${s}:00` : s);
      await api.post(`/a2/autopilot/manual-rescue/${ot.odoo_task_id}/schedule`, {
        tech_email: techEmail.trim(),
        slot_start: iso(slotStart),
        slot_end: iso(slotEnd),
        force,
        notes: notes.trim() || null,
      });
      toast.success(`OT ${ot.afc_code || ot.odoo_task_id} confirmada ✓ (aviso enviado al cliente)`);
      onSuccess();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo agendar");
    } finally { setSaving(false); }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
      onClick={() => !saving && onClose()}
      data-testid="rescue-modal-backdrop"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-lg w-full max-w-xl p-5 shadow-xl"
        data-testid="rescue-modal"
      >
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-bold text-[#0A3747] flex items-center gap-2" style={{ fontFamily: "Space Grotesk" }}>
            <Calendar size={16} /> Agendar Manualmente
          </h3>
          <button onClick={onClose} disabled={saving} className="text-gray-500 hover:text-gray-800 text-xl leading-none disabled:opacity-40" data-testid="rescue-modal-close">×</button>
        </div>

        <div className="mb-3 p-2.5 bg-gray-50 border border-gray-200 rounded text-xs">
          <div className="font-mono font-semibold text-[#0A3747]">{ot.afc_code || `#${ot.odoo_task_id}`}</div>
          <div className="text-gray-700">{ot.client_name}</div>
          <div className="mt-1 text-red-700 flex items-center gap-1">
            <AlertOctagon size={11} /> {ot.autopilot_fail_reason || "Sin razón"}
          </div>
        </div>

        {loading ? (
          <div className="p-4 text-center text-gray-400 flex items-center justify-center gap-2">
            <Loader2 size={16} className="animate-spin" /> Cargando sugerencia…
          </div>
        ) : (
          <>
            {suggestion?.suggestion?.reason && (
              <div className="mb-3 p-2 bg-blue-50 border border-blue-200 rounded text-[11px] text-blue-800" data-testid="rescue-suggestion-info">
                💡 <b>Slot sugerido:</b> {suggestion.suggestion.reason}
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <label className="col-span-2 text-xs">
                <span className="text-gray-600 font-medium mb-1 block flex items-center gap-1"><User size={11}/> Técnico (email)</span>
                <input
                  type="email"
                  value={techEmail}
                  onChange={(e) => setTechEmail(e.target.value)}
                  placeholder="tecnico@alfanet.com"
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-[#E0E621]"
                  data-testid="rescue-input-tech"
                  autoFocus
                />
              </label>
              <label className="text-xs">
                <span className="text-gray-600 font-medium mb-1 block flex items-center gap-1"><Clock size={11}/> Inicio</span>
                <input
                  type="datetime-local"
                  value={slotStart}
                  onChange={(e) => setSlotStart(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-[#E0E621]"
                  data-testid="rescue-input-start"
                />
              </label>
              <label className="text-xs">
                <span className="text-gray-600 font-medium mb-1 block flex items-center gap-1"><Clock size={11}/> Fin</span>
                <input
                  type="datetime-local"
                  value={slotEnd}
                  onChange={(e) => setSlotEnd(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-[#E0E621]"
                  data-testid="rescue-input-end"
                />
              </label>
              <label className="col-span-2 text-xs">
                <span className="text-gray-600 font-medium mb-1 block">Notas (opcional)</span>
                <input
                  type="text"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Motivo o comentario para la bitácora"
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                  data-testid="rescue-input-notes"
                />
              </label>
              <label className="col-span-2 flex items-center gap-2 text-xs bg-amber-50 border border-amber-200 rounded p-2 cursor-pointer" data-testid="rescue-force-label">
                <input
                  type="checkbox"
                  checked={force}
                  onChange={(e) => setForce(e.target.checked)}
                  className="accent-amber-600"
                  data-testid="rescue-force"
                />
                <div>
                  <div className="font-semibold text-amber-900">Forzar cita (ignorar buffers de traslado y horarios)</div>
                  <div className="text-[10px] text-amber-700">Úsalo si el coordinador sabe que el técnico puede hacerlo pese a la regla.</div>
                </div>
              </label>
            </div>

            <div className="flex justify-end gap-2 mt-4">
              <button onClick={onClose} disabled={saving}
                className="px-4 py-2 border border-gray-300 text-sm rounded hover:bg-gray-50 disabled:opacity-40"
                data-testid="rescue-cancel">Cancelar</button>
              <button
                onClick={submit}
                disabled={saving}
                data-testid="rescue-submit"
                className="px-4 py-2 bg-emerald-600 text-white text-sm rounded hover:bg-emerald-700 disabled:opacity-40 flex items-center gap-1.5 font-semibold"
              >
                {saving ? <Loader2 size={13} className="animate-spin" /> : <CheckCircle2 size={13} />}
                Confirmar cita
              </button>
            </div>

            <div className="mt-2 text-[10px] text-gray-400 italic">
              La OT quedará en estado <b>«Confirmada»</b> al guardar. Se enviará un aviso informativo (WhatsApp+Email · mock) sin esperar respuesta del cliente.
            </div>
          </>
        )}
      </div>
    </div>
  );
}
